import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { useSelector } from "react-redux";
import { toAbsoluteUrl } from "../../../../_helpers";

const STORAGE_KEY = "oversight.aiChat.v1";
const ASSISTANT_NAME = "Trove Chat";
const BRAND_GREEN = "#22A455";
const CHAT_ICON_URL = "/media/logos/logo-letter-2.png";

const safeJsonParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const uuidv4 = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

const nowIso = () => new Date().toISOString();

const normalizeAnswer = (payload) => {
  const candidate =
    payload?.result?.response ??
    payload?.result?.summary ??
    payload?.response ??
    payload?.summary ??
    payload?.data?.result?.response ??
    payload?.data?.result?.summary ??
    payload?.data?.summary ??
    payload?.data?.answer ??
    payload?.data?.message ??
    payload?.answer ??
    payload?.message ??
    payload?.data ??
    payload;

  if (candidate === null || candidate === undefined) return "No response";
  if (typeof candidate === "string") {
    const trimmed = candidate.trim();
    if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
      try {
        const parsed = JSON.parse(trimmed);
        const nested = parsed?.response ?? parsed?.summary ?? parsed?.message ?? parsed?.answer;
        if (typeof nested === "string" && nested.trim()) return nested;
      } catch {
        // fall through
      }
    }
    return candidate;
  }
  if (typeof candidate === "object") {
    const nested =
      candidate?.response ??
      candidate?.summary ??
      candidate?.message ??
      candidate?.answer ??
      candidate?.result?.response ??
      candidate?.result?.summary;
    if (typeof nested === "string" && nested.trim()) {
      const trimmed = nested.trim();
      if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
        try {
          const parsed = JSON.parse(trimmed);
          const parsedNested = parsed?.response ?? parsed?.summary ?? parsed?.message ?? parsed?.answer;
          if (typeof parsedNested === "string" && parsedNested.trim()) return parsedNested;
        } catch {
          // fall through
        }
      }
      return nested;
    }
  }
  try {
    return JSON.stringify(candidate, null, 2);
  } catch {
    return "Unable to parse response";
  }
};

const serializeMessages = (messages) =>
  messages.map(({ id, role, text, createdAt }) => ({
    id,
    role,
    text,
    createdAt,
  }));

export default function AiChatWidget() {
  const adminUser = useSelector((state) => state?.auth?.user);
  const resolvedUserGroup =
    adminUser?.userGroup ??
    adminUser?.group ??
    adminUser?.user_group ??
    adminUser?.usergroup ??
    "trove";

  const persisted = useMemo(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? safeJsonParse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  }, []);

  const [isOpen, setIsOpen] = useState(false);
  const [conversationId, setConversationId] = useState(persisted.conversationId || uuidv4());
  const [messages, setMessages] = useState(() => {
    const initial = Array.isArray(persisted.messages) ? persisted.messages : [];
    return initial.map((m) => ({ ...m, isTyping: false, fullText: undefined }));
  });
  const [draft, setDraft] = useState("");
  const [isSending, setIsSending] = useState(false);

  const bottomRef = useRef(null);
  const typingTimerRef = useRef(null);
  const typingMessageIdRef = useRef(null);
  const inputRef = useRef(null);

  const stopTyping = () => {
    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    typingMessageIdRef.current = null;
  };

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        conversationId,
        messages: serializeMessages(messages).slice(-100),
      })
    );
  }, [conversationId, messages]);

  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      if (bottomRef.current) {
        bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
      }
    }, 50);
    return () => clearTimeout(t);
  }, [isOpen, messages.length]);

  useEffect(() => {
    if (typingTimerRef.current) return;
    const typingMessage = messages.find((m) => m?.isTyping);
    if (!typingMessage) return;
    if (typingMessageIdRef.current === typingMessage.id) return;

    typingMessageIdRef.current = typingMessage.id;
    const fullText = `${typingMessage.fullText ?? ""}`;
    let index = (typingMessage.text || "").length;

    typingTimerRef.current = setInterval(() => {
      index += 1;
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== typingMessage.id) return m;
          const nextText = fullText.slice(0, Math.min(index, fullText.length));
          if (nextText.length >= fullText.length) {
            return { ...m, text: fullText, isTyping: false, fullText: undefined };
          }
          return { ...m, text: nextText };
        })
      );

      if (index >= fullText.length) {
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
        typingMessageIdRef.current = null;
      }
    }, 14);

    return () => {
      stopTyping();
    };
  }, [messages]);

  const clearConversation = () => {
    stopTyping();
    setIsSending(false);
    setMessages([]);
    setDraft("");
  };

  const startNewConversation = () => {
    stopTyping();
    setIsSending(false);
    setConversationId(uuidv4());
    setMessages([]);
    setDraft("");
  };

  const pushAssistantTypingMessage = (fullText) => {
    const assistantMessage = {
      id: uuidv4(),
      role: "assistant",
      text: "",
      fullText,
      isTyping: true,
      createdAt: nowIso(),
    };
    setMessages((prev) => [...prev, assistantMessage]);
  };

  const sendText = async (rawText) => {
    const text = `${rawText}`.trim();
    if (!text || isSending) return;

    const userMessage = {
      id: uuidv4(),
      role: "user",
      text,
      createdAt: nowIso(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setDraft("");
    setIsSending(true);

    try {
      const result = await axios.post("/ai/staff-support-message", {
        ref: conversationId,
        message: text,
        userGroup: resolvedUserGroup,
      });
      pushAssistantTypingMessage(normalizeAnswer(result?.data));
    } catch {
      pushAssistantTypingMessage("No answer at the moment.");
    } finally {
      setIsSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendText(draft);
    }
  };

  const hasMessages = messages.length > 0;

  return (
    <div style={{ position: "fixed", right: 24, bottom: 24, zIndex: 9999 }}>
      {isOpen && (
        <div
          className="card shadow-lg"
          style={{
            width: 360,
            height: 540,
            borderRadius: 16,
            overflow: "hidden",
            marginBottom: 12,
          }}
        >
          <div
            className="d-flex align-items-center justify-content-between px-4"
            style={{ height: 56, background: BRAND_GREEN, color: "#fff" }}
          >
            <button
              type="button"
              className="btn btn-icon btn-sm"
              onClick={() => setIsOpen(false)}
              style={{ color: "#fff" }}
              title="Back"
            >
              <span className="svg-icon svg-icon-md svg-icon-white">
                <SVG src={toAbsoluteUrl("/media/svg/icons/Navigation/Arrow-left.svg")} />
              </span>
            </button>
            <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
              <img
                src={toAbsoluteUrl(CHAT_ICON_URL)}
                alt="Trove"
                style={{ width: 22, height: 22, marginRight: 10 }}
              />
              <div className="font-weight-bolder" style={{ fontSize: 16, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {ASSISTANT_NAME}
              </div>
            </div>
            <div className="d-flex align-items-center">
              <button
                type="button"
                className="btn btn-sm"
                onClick={clearConversation}
                title="Clear chat"
                style={{
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.6)",
                  background: "rgba(255,255,255,0.12)",
                  padding: "6px 10px",
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span className="svg-icon svg-icon-sm svg-icon-white mr-2">
                  <SVG src={toAbsoluteUrl("/media/svg/icons/General/Trash.svg")} />
                </span>
                Clear
              </button>
              <button
                type="button"
                className="btn btn-icon btn-sm"
                onClick={startNewConversation}
                style={{ color: "#fff" }}
                title="New conversation"
              >
                <span className="svg-icon svg-icon-md svg-icon-white">
                  <SVG src={toAbsoluteUrl("/media/svg/icons/Layout/Layout-4-blocks.svg")} />
                </span>
              </button>
            </div>
          </div>

          <div className="d-flex flex-column" style={{ height: "calc(100% - 56px)", background: "#F7F7F8" }}>
            <div className="flex-grow-1 px-4 py-4" style={{ overflowY: "auto" }}>
              {!hasMessages && (
                <>
                  <div className="d-flex align-items-start mb-6">
                    <div
                      className="d-flex align-items-center justify-content-center mr-3"
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 999,
                        background: "#fff",
                        flexShrink: 0,
                        border: `2px solid ${BRAND_GREEN}`,
                      }}
                    >
                      <img
                        src={toAbsoluteUrl(CHAT_ICON_URL)}
                        alt="Trove"
                        style={{ width: 22, height: 22 }}
                      />
                    </div>
                    <div
                      style={{
                        background: "#E6E6E6",
                        borderRadius: 12,
                        padding: "12px 14px",
                        maxWidth: "78%",
                      }}
                    >
                      <div className="text-dark font-size-sm">
                        👋 Hi! I&apos;m {ASSISTANT_NAME}, an AI Assistant. Ask me anything.
                      </div>
                    </div>
                  </div>
                </>
              )}

              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`d-flex mb-4 ${m.role === "user" ? "justify-content-end" : "justify-content-start"}`}
                >
                  {m.role !== "user" && (
                    <div
                      className="d-flex align-items-center justify-content-center mr-3"
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: 999,
                        background: "#fff",
                        flexShrink: 0,
                        marginTop: 2,
                        border: `2px solid ${BRAND_GREEN}`,
                      }}
                    >
                      <img
                        src={toAbsoluteUrl(CHAT_ICON_URL)}
                        alt="Trove"
                        style={{ width: 18, height: 18 }}
                      />
                    </div>
                  )}
                  <div
                    style={{
                      maxWidth: "78%",
                      background: m.role === "user" ? "#FFFFFF" : "#E6E6E6",
                      borderRadius: 12,
                      padding: "12px 14px",
                      whiteSpace: "pre-wrap",
                      border: m.role === "user" ? `1px solid rgba(0,0,0,0.08)` : "none",
                    }}
                  >
                    <div className="text-dark font-size-sm">{m.text}</div>
                  </div>
                </div>
              ))}

              {isSending && (
                <div className="d-flex mb-4 justify-content-start">
                  <div
                    className="d-flex align-items-center justify-content-center mr-3"
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 999,
                      background: "#fff",
                      flexShrink: 0,
                      marginTop: 2,
                      border: `2px solid ${BRAND_GREEN}`,
                    }}
                  >
                    <img
                      src={toAbsoluteUrl(CHAT_ICON_URL)}
                      alt="Trove"
                      style={{ width: 18, height: 18 }}
                    />
                  </div>
                  <div
                    style={{
                      maxWidth: "78%",
                      background: "#E6E6E6",
                      borderRadius: 12,
                      padding: "12px 14px",
                    }}
                  >
                    <span className="text-dark font-size-sm">
                      <span style={{ opacity: 0.5 }}>Typing</span>
                      <span style={{ letterSpacing: 2 }}>...</span>
                    </span>
                  </div>
                </div>
              )}

              <div ref={bottomRef} />
            </div>

            <div
              className="px-4 py-3"
              style={{
                background: "#fff",
                borderTop: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <div className="d-flex align-items-center">
                <textarea
                  ref={inputRef}
                  className="form-control border-0"
                  rows={1}
                  placeholder=""
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onKeyDown}
                  disabled={isSending}
                  style={{ resize: "none", boxShadow: "none", background: "transparent" }}
                />
                <button
                  type="button"
                  className="btn btn-icon btn-sm"
                  title="Like"
                  onClick={() => sendText("👍")}
                  disabled={isSending}
                >
                  <span className="svg-icon svg-icon-sm svg-icon-muted">
                    <SVG src={toAbsoluteUrl("/media/svg/icons/General/Like.svg")} />
                  </span>
                </button>
                <button type="button" className="btn btn-icon btn-sm" title="Attach" disabled>
                  <span className="svg-icon svg-icon-sm svg-icon-muted">
                    <SVG src={toAbsoluteUrl("/media/svg/icons/General/Attachment1.svg")} />
                  </span>
                </button>
                <button type="button" className="btn btn-icon btn-sm" title="Emoji" disabled>
                  <span className="svg-icon svg-icon-sm svg-icon-muted">
                    <SVG src={toAbsoluteUrl("/media/svg/icons/General/Smile.svg")} />
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <button
        type="button"
        className="btn btn-icon shadow-lg"
        onClick={() => setIsOpen((v) => !v)}
        style={{
          width: 56,
          height: 56,
          borderRadius: 999,
          background: BRAND_GREEN,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        title={isOpen ? "Close" : "Chat"}
      >
        {isOpen ? (
          <span className="svg-icon svg-icon-lg svg-icon-white">
            <SVG src={toAbsoluteUrl("/media/svg/icons/Navigation/Down-2.svg")} />
          </span>
        ) : (
          <img
            src={toAbsoluteUrl(CHAT_ICON_URL)}
            alt="Trove"
            style={{ width: 28, height: 28 }}
          />
        )}
      </button>
    </div>
  );
}
